/* eslint-disable react/jsx-indent */
/* eslint-disable react/jsx-wrap-multilines */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/display-name */
import i18n from 'i18n/config';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Col } from 'reactstrap';
import DataTable from 'react-data-table-component';

import { getTheme } from 'utils/theme';

import styles from './styles';
import Export from './components/ExportTable';
import SearchBar from './components/SearchBar';
import CustomCheckbox from './components/CustomCheckbox';
import ExpandableRowComponent from './components/ExpandableRowComponent';
import { cleanData } from './utils/cleanData';
import { exportExcel } from './utils/exportExcel';
import TableLoader from './TableLoader';
import DataTablePagination from './DataTablePagination';

const CustomDataTable = ({
  data,
  columns,
  pagination,
  hasCustomPagination = false,
  customPagination = {},
  actions,
  className,
  cardClassName = '',
  selectableRows = false,
  expandableRows = false,
  // custom params
  rowsPerPage = 10,
  searchBy,
  searchByPlaceholder = '',
  defaultSortFieldId = 0,
  defaultSortAsc = true,
  showDense = false,
  showDownload = true,
  showNationality = false,
  toggleNationality,
  customData = {},
  fileName = 'Export',
  filterText = {},
  filterIcon = {},
  filterByParam = '',
  onFilterChange,
  onSort = undefined,
  onDownloadTable = () => {},
  sortServer = false,
  isLoading = false,
}) => {
  const [tableData, setTableData] = useState(data);
  const [searchText, setSearchText] = useState('');
  const [mobileView, setMobileView] = useState();
  const [dense, setDense] = useState(false);
  const [statusFilter, setStatusFilter] = useState(true);
  const [selectedTheme, setSelectedTheme] = useState(getTheme());

  const paginationComponentOptions = {
    rowsPerPageText: i18n.t('Show'),
    rangeSeparatorText: i18n.t('Of'),
    selectAllRowsItem: true,
    selectAllRowsItemText: i18n.t('Todos'),
  };

  const handleExportExcel = () => {
    if (customData) {
      exportExcel({
        data: customData,
        fileName,
      });
    } else {
      exportExcel({
        data: cleanData({ data, columns }),
        fileName,
      });
    }
  };

  const handleDownloadTable = () => {
    if (onDownloadTable) {
      onDownloadTable();
    } else {
      handleExportExcel();
    }
  };

  useEffect(() => {
    if (onFilterChange) {
      setTableData(data);
      return;
    }
    let defaultData = tableData;
    if (searchText !== '') {
      defaultData = data?.filter((item) => {
        const normalizedSearchText = searchText
          ?.normalize('NFD')
          ?.replace(/[\u0300-\u036f]/g, '')
          ?.toLowerCase();

        const isMatch = Array.isArray(searchBy)
          ? searchBy.some((key) => {
              const normalizedItemText = item[key]
                ?.normalize('NFD')
                ?.replace(/[\u0300-\u036f]/g, '')
                ?.toLowerCase();
              return normalizedItemText?.includes(normalizedSearchText);
            })
          : item[searchBy]
              ?.normalize('NFD')
              ?.replace(/[\u0300-\u036f]/g, '')
              ?.toLowerCase()
              ?.includes(normalizedSearchText);

        return isMatch;
      });

      setTableData(defaultData);
    } else {
      setTableData(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, onFilterChange]);

  // function to change the design view under 1200 px
  const viewChange = () => {
    if (window.innerWidth < 960 && expandableRows) {
      setMobileView(true);
    } else {
      setMobileView(false);
    }
  };

  const getFilteredData = useMemo(
    () =>
      filterByParam && statusFilter
        ? data.filter((item) => item[filterByParam] === true)
        : data,
    [data, statusFilter, filterByParam]
  );

  const handleSearchTerm = useCallback(
    (term) => {
      setSearchText(term);
      if (onFilterChange) {
        onFilterChange(term);
      }
    },
    [onFilterChange]
  );

  useEffect(() => {
    setTableData(getFilteredData);
  }, [getFilteredData]);

  useEffect(() => {
    window.addEventListener('load', viewChange);
    window.addEventListener('resize', viewChange);
    return () => {
      window.removeEventListener('resize', viewChange);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    window.addEventListener('storage', () => {
      const theme = getTheme();
      setSelectedTheme(theme);
    });
  }, []);

  useEffect(() => {
    setTableData(data);
  }, [data]);

  return (
    <div
      className={`dataTables_wrapper dt-bootstrap4 no-footer ${
        className || ''
      }`}
    >
      <div className="nk-block">
        <div className={`card card-bordered card-stretch ${cardClassName}`}>
          <div className="card-inner-group">
            <div className="card-inner position-relative card-tools-toggle">
              {/* TABLE HEADER */}
              <div className="card-title-group">
                {searchBy && (
                  <SearchBar
                    searchBy={searchBy}
                    onSearch={handleSearchTerm}
                    placeholder={searchByPlaceholder}
                  />
                )}

                <Col className="col-6 text-right" sm="8">
                  <div className="datatable-filter">
                    <div className="d-flex justify-content-end g-2">
                      {showNationality && toggleNationality && (
                        <div className="dt-export-buttons d-flex align-center">
                          <div className="dt-buttons btn-group flex-wrap">
                            <button
                              type="button"
                              className="btn btn-white btn-trigger p-0"
                              onClick={toggleNationality}
                            >
                              {showNationality ? (
                                <em className="icon ni ni-flag" />
                              ) : (
                                <em className="icon ni ni-flag-fill" />
                              )}
                            </button>
                          </div>
                        </div>
                      )}
                      {actions && (
                        <Export
                          data={cleanData({ data, columns })}
                          fileName={fileName || 'Export'}
                          filterText={filterText}
                          filterIcon={filterIcon}
                          filterByParam={filterByParam}
                          statusFilter={statusFilter}
                          setStatusFilter={setStatusFilter}
                        />
                      )}
                      {showDownload && (
                        <div className="dt-export-buttons d-flex align-center">
                          <div className="dt-buttons btn-group flex-wrap">
                            <button
                              type="button"
                              className="btn btn-white btn-trigger p-0"
                              onClick={handleDownloadTable}
                            >
                              <em className="icon ni ni-download" />
                            </button>
                          </div>
                        </div>
                      )}
                      {showDense && (
                        <div className="dt-export-buttons d-flex align-center">
                          <div className="dt-buttons btn-group flex-wrap">
                            <button
                              type="button"
                              className="btn btn-white btn-trigger p-0"
                              onClick={() => setDense(!dense)}
                            >
                              {dense ? (
                                <em className="icon ni ni-view-list-wd" />
                              ) : (
                                <em className="icon ni ni-view-list-fill" />
                              )}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </Col>
              </div>
            </div>

            <DataTable
              data={tableData}
              columns={columns}
              className={className}
              selectableRows={selectableRows}
              selectableRowsComponent={CustomCheckbox}
              expandableRowsComponent={ExpandableRowComponent}
              expandableRows={mobileView}
              sortServer={sortServer}
              onSort={onSort}
              paginationServer={hasCustomPagination}
              paginationTotalRows={
                hasCustomPagination ? customPagination.totalItems ?? 0 : undefined
              }
              paginationPerPage={
                hasCustomPagination
                  ? customPagination.itemPerPage ?? rowsPerPage
                  : rowsPerPage
              }
              noDataComponent={
                <div className="p-2">No se han encontrado registros</div>
              }
              sortIcon={
                <div>
                  <span>&darr;</span>
                  <span>&uarr;</span>
                </div>
              }
              defaultSortFieldId={defaultSortFieldId}
              defaultSortAsc={defaultSortAsc}
              customStyles={styles(selectedTheme)}
              pagination={pagination}
              paginationComponentOptions={
                hasCustomPagination ? null : paginationComponentOptions
              }
              paginationComponent={
                hasCustomPagination
                  ? ({
                      currentPage,
                      rowsPerPage,
                      rowCount,
                      onChangePage,
                      onChangeRowsPerPage,
                    }) => (
                      <DataTablePagination
                        className={customPagination.className}
                        currentPage={customPagination.currentPage}
                        totalItems={customPagination.totalItems}
                        itemPerPage={customPagination.itemPerPage}
                        customItemPerPage={customPagination.itemPerPage}
                        paginate={customPagination.onChangePage}
                        onChangeRowsPerPage={
                          customPagination.onChangeRowsPerPage
                        }
                      />
                    )
                  : undefined
              }
              progressPending={isLoading}
              progressComponent={<TableLoader rows={10} notHeader />}
              dense={dense}
              highlightOnHover
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomDataTable;
